import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookOpen, ChevronLeft, ChevronRight, FileText, FolderOpen, Plus, ScanSearch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ErrorState } from '@/components/error-state'
import { useWikiInvoke, useWikiTopics, useWikiTopicTree } from '@/features/wiki/use-wiki'
import { useVaultFile } from '@/features/vault/use-vault'
import { VaultPreview } from '@/features/vault/vault-preview'
import { cn } from '@/lib/utils'

type MobileView = 'topics' | 'tree' | 'page'

export function WikiPage() {
  const { topics, isLoading, isError, refetch, isFetching } = useWikiTopics()
  const [activeSlug, setActiveSlug] = useState<string | null>(null)
  const [activePagePath, setActivePagePath] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [mobileView, setMobileView] = useState<MobileView>('topics')
  const createOpenerRef = useRef<HTMLElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  const effectiveSlug = activeSlug ?? topics[0]?.slug ?? null
  const tree = useWikiTopicTree(effectiveSlug)

  function openCreateDialog(event: React.MouseEvent<HTMLElement>) {
    createOpenerRef.current = event.currentTarget
    setCreateOpen(true)
  }

  function selectTopic(slug: string) {
    setActiveSlug(slug)
    setActivePagePath(null)
    setMobileView('tree')
  }

  function selectPage(path: string) {
    setActivePagePath(path)
    setMobileView('page')
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="min-w-0 flex-1 truncate text-[26px] font-[650] leading-tight"
        >
          Wissen
        </h1>
        {effectiveSlug && (
          <span
            className="max-w-[40%] truncate text-[12px] text-muted-foreground"
            title={effectiveSlug}
          >
            {effectiveSlug}
          </span>
        )}
        {activePagePath && (
          <span
            className="hidden max-w-[40%] truncate font-mono text-[12px] text-muted-foreground sm:inline"
            title={activePagePath}
          >
            {activePagePath.split('/').pop()}
          </span>
        )}
      </div>
      {/* Mobile stepper */}
      <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2 lg:hidden">
        <Button
          variant="ghost"
          size="sm"
          disabled={mobileView === 'topics'}
          onClick={() => setMobileView(mobileView === 'page' ? 'tree' : 'topics')}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Zurück
        </Button>
        <span className="text-sm font-medium">
          {mobileView === 'topics' ? 'Themen' : mobileView === 'tree' ? 'Übersicht' : 'Inhalt'}
        </span>
        <Button
          variant="ghost"
          size="sm"
          disabled={mobileView === 'page' || (mobileView === 'tree' && !activePagePath)}
          onClick={() => setMobileView(mobileView === 'topics' ? 'tree' : 'page')}
        >
          Weiter
          <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {/* Topics column */}
        <aside
          className={cn(
            'flex w-full min-w-0 flex-col border-r border-border bg-muted/30 lg:w-64 lg:flex-shrink-0',
            mobileView !== 'topics' && 'hidden lg:flex',
          )}
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <BookOpen className="h-4 w-4 shrink-0" />
              <h2 className="truncate text-[16px] font-[650] leading-tight">Wissensthemen</h2>
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Neues Wissensthema"
              onClick={openCreateDialog}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {isLoading ? (
              <p className="px-2 py-4 text-xs text-muted-foreground">Lädt…</p>
            ) : isError ? (
              <div className="space-y-2 px-2 py-4 text-center">
                <p className="text-xs text-destructive">Wissensthemen konnten nicht geladen werden.</p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void refetch()}
                  disabled={isFetching}
                >
                  Erneut versuchen
                </Button>
              </div>
            ) : topics.length === 0 ? (
              <div className="space-y-2 px-2 py-4 text-center">
                <p className="text-xs text-muted-foreground">Noch kein Thema.</p>
                <Button size="sm" onClick={openCreateDialog}>
                  Erstes Thema anlegen
                </Button>
              </div>
            ) : (
              <ul className="space-y-1">
                {topics.map((t) => (
                  <li key={t.slug}>
                    <button
                      type="button"
                      onClick={() => selectTopic(t.slug)}
                      aria-current={effectiveSlug === t.slug ? 'true' : undefined}
                      title={t.slug}
                      className={cn(
                        'flex min-h-11 w-full items-center justify-between gap-2 rounded px-2 text-left text-sm hover:bg-muted min-[761px]:min-h-9',
                        effectiveSlug === t.slug
                          ? 'bg-accent font-medium text-accent-foreground'
                          : undefined,
                      )}
                    >
                      <span className="min-w-0 flex-1 truncate">{t.slug}</span>
                      <span className="shrink-0 text-[12px] text-muted-foreground">{t.pageCount}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Tree column */}
        <aside
          className={cn(
            'flex w-full min-w-0 flex-col border-r border-border lg:w-64 lg:flex-shrink-0',
            mobileView !== 'tree' && 'hidden lg:flex',
          )}
        >
          <div className="border-b border-border px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
              <h3 className="truncate text-[16px] font-[650] leading-tight" title={effectiveSlug ?? undefined}>
                {effectiveSlug ?? 'Kein Thema'}
              </h3>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 text-sm">
            {!effectiveSlug ? (
              <p className="text-xs text-muted-foreground">Thema wählen.</p>
            ) : (
              <TopicTree
                tree={tree}
                activePath={activePagePath}
                onSelect={(p) => selectPage(p)}
              />
            )}
          </div>
        </aside>

        {/* Page column */}
        <main
          className={cn(
            'min-w-0 flex-1 overflow-hidden',
            mobileView !== 'page' && 'hidden lg:block',
          )}
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-hidden">
              {activePagePath ? (
                <PageRender path={activePagePath} onNavigate={selectPage} />
              ) : (
                <EmptyState topicSlug={effectiveSlug} onSelectTopic={() => setMobileView('topics')} />
              )}
            </div>
          </div>
        </main>

        <CreateTopicDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(slug) => selectTopic(slug)}
          openerRef={createOpenerRef}
          fallbackFocusRef={headingRef}
        />
      </div>
    </div>
  )
}

interface TopicTreeProps {
  tree: ReturnType<typeof useWikiTopicTree>
  activePath: string | null
  onSelect: (path: string) => void
}

function TopicTree({ tree, activePath, onSelect }: TopicTreeProps) {
  return (
    <div className="space-y-3">
      {tree.agents && (
        <Section title="AGENTS.md">
          <PageItem path={tree.agents} name="AGENTS.md" active={activePath === tree.agents} onSelect={onSelect} />
        </Section>
      )}
      <Section title={`wiki/ (${tree.pages.filter((p) => !p.isLintReport).length})`}>
        {tree.pages.length === 0 ? (
          <p className="px-2 text-xs italic text-muted-foreground">Leer.</p>
        ) : (
          tree.pages.map((p) => (
            <PageItem
              key={p.path}
              path={p.path}
              name={p.name}
              active={activePath === p.path}
              onSelect={onSelect}
              icon={p.isLintReport ? <ScanSearch className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
            />
          ))
        )}
      </Section>
      <Section title={`source/ (${tree.sources.length})`}>
        {tree.sources.length === 0 ? (
          <p className="px-2 text-xs italic text-muted-foreground">Leer.</p>
        ) : (
          tree.sources.map((s) => (
            <PageItem
              key={s.path}
              path={s.path}
              name={s.name}
              active={activePath === s.path}
              onSelect={onSelect}
            />
          ))
        )}
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 px-2 text-[16px] font-[650] leading-tight tracking-tight text-muted-foreground">
        {title}
      </p>
      <div className="space-y-0.5">{children}</div>
    </div>
  )
}

interface PageItemProps {
  path: string
  name: string
  active: boolean
  onSelect: (p: string) => void
  icon?: React.ReactNode
}

function PageItem({ path, name, active, onSelect, icon }: PageItemProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(path)}
      aria-current={active ? 'page' : undefined}
      title={name}
      className={cn(
        'flex min-h-11 w-full items-center gap-2 rounded px-2 text-left text-sm hover:bg-muted min-[761px]:min-h-9',
        active ? 'bg-accent font-medium text-accent-foreground' : undefined,
      )}
    >
      {icon ?? <FileText className="h-3 w-3 shrink-0" />}
      <span className="min-w-0 flex-1 truncate break-all">{name}</span>
    </button>
  )
}

function PageRender({ path, onNavigate }: { path: string; onNavigate: (p: string) => void }) {
  const navigate = useNavigate()
  const { data: file, isLoading, isError, error, refetch, isFetching } = useVaultFile(path)

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Lädt…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <ErrorState
          title="Seite konnte nicht geladen werden"
          description={error instanceof Error ? error.message : 'Bitte versuche es erneut.'}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          className="w-full max-w-md"
        />
      </div>
    )
  }

  if (!file) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Seite nicht gefunden.
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <VaultPreview
        content={file.content}
        filePath={file.path}
        contentType={file.content_type}
        storagePath={file.storage_path}
        imageMetadata={file.image_metadata}
        onNavigate={(target) => {
          if (target.startsWith('_Knowledge/')) {
            onNavigate(target)
            return
          }
          // Known vault-root paths open the source panel; relative topic links
          // continue to resolve inside the current knowledge topic.
          if (/^(?:Notes|_[^/]+)\//.test(target)) {
            navigate(`/quellen?path=${encodeURIComponent(target)}`)
            return
          }
          const topic = path.match(/^(_Knowledge\/[^/]+)/)?.[1]
          if (!topic) return
          if (target.includes('/')) {
            onNavigate(`${topic}/${target}`)
          } else {
            onNavigate(`${topic}/wiki/${target}.md`)
          }
        }}
      />
    </div>
  )
}

function EmptyState({ topicSlug, onSelectTopic }: { topicSlug: string | null; onSelectTopic: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center">
      <BookOpen className="h-8 w-8 text-muted-foreground" />
      <p className="mt-3 text-sm font-medium">
        {topicSlug ? `Thema „${topicSlug}“` : 'Wissen'}
      </p>
      <p className="mt-1 max-w-xs text-xs text-muted-foreground">
        Wähle eine Seite aus der Übersicht oder lege ein neues Thema an.
      </p>
      <Button variant="outline" size="sm" className="mt-4 lg:hidden" onClick={onSelectTopic}>
        Themen anzeigen
      </Button>
    </div>
  )
}

function CreateTopicDialog({
  open,
  onOpenChange,
  onCreated,
  openerRef,
  fallbackFocusRef,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (slug: string) => void
  openerRef: React.RefObject<HTMLElement | null>
  fallbackFocusRef: React.RefObject<HTMLHeadingElement | null>
}) {
  const [value, setValue] = useState('')
  const { invoke, state } = useWikiInvoke()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const slug = value.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    if (!slug) return
    try {
      await invoke({ operation: 'create', topic: slug })
      onCreated(slug)
      onOpenChange(false)
      setValue('')
    } catch {
      // Fehler wird via useWikiInvoke als Toast angezeigt; Dialog bleibt offen.
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && state.isRunning) return
        onOpenChange(next)
      }}
    >
      <DialogContent
        closeDisabled={state.isRunning}
        onEscapeKeyDown={(event) => {
          if (state.isRunning) event.preventDefault()
        }}
        onPointerDownOutside={(event) => {
          if (state.isRunning) event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          const opener = openerRef.current
          if (opener?.isConnected && opener.getClientRects().length > 0) opener.focus()
          else fallbackFocusRef.current?.focus()
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Neues Wissensthema</DialogTitle>
            <DialogDescription>
              Verwende Buchstaben von a bis z, Zahlen und Bindestriche. Leerzeichen werden zu Bindestrichen.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label htmlFor="wiki-topic-slug" className="mb-1 block text-xs text-muted-foreground">
              Themenname
            </label>
            <Input
              id="wiki-topic-slug"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="z.B. produkt-roadmap"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={state.isRunning}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={state.isRunning || !value.trim()}>
              {state.isRunning ? 'Wird erstellt…' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
